import { DeviceStatus } from '@/api/deviceApi'
import { organizationsApi } from '@/api/organizationsApi'
import { OrganizationBrokerFields } from '@/components/organization/OrganizationBrokerFields'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label, RequiredIndicator } from '@/components/ui/label'
import { ORGANIZATION_CREATION_LIMIT_PER_USER } from '@/config/plans'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAuthActions, useAuthStore } from '@/stores/authStore'
import { validateBrokerHost, validateBrokerPort } from '@/utils/brokerSettings'
import { isConsoleDeployment, isDemoDeployment } from '@/utils/deployment'
import { getCreatedOrganizationsCount } from '@/utils/permissions'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

interface CreateOrganizationDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSuccess?: () => void
}

interface CreateOrganizationFormValues {
	name: string
	brokerHost: string
	brokerPort: number
}

export function CreateOrganizationDialog({ open, onOpenChange, onSuccess }: CreateOrganizationDialogProps) {
	const isMobile = useIsMobile()
	const navigate = useNavigate()
	const [name, setName] = useState('')
	const [brokerHost, setBrokerHost] = useState('')
	const [brokerPort, setBrokerPort] = useState('')
	const [validationError, setValidationError] = useState<string | null>(null)
	const [brokerHostError, setBrokerHostError] = useState<string | null>(null)
	const [brokerPortError, setBrokerPortError] = useState<string | null>(null)

	const user = useAuthStore(state => state.user)
	const { refreshUser } = useAuthActions()
	const isConsole = isConsoleDeployment() || isDemoDeployment()

	const createMutation = useMutation({
		mutationFn: (values: CreateOrganizationFormValues) =>
			organizationsApi.createOrganization(values.name, {
				broker_host: values.brokerHost,
				broker_port: values.brokerPort,
			}),
		onSuccess: async (createdOrg, values) => {
			toast.success(`Organization "${values.name}" created successfully`)
			await refreshUser()
			setName('')
			setBrokerHost('')
			setBrokerPort('')

			// Navigate before closing dialog to prevent unmounting issues
			if (isConsole && createdOrg?.id) {
				if (isDemoDeployment()) {
					navigate({
						to: '/orgs/$orgId/inventory/$status',
						params: { orgId: createdOrg.id, status: DeviceStatus.Active },
					})
				} else {
					navigate({
						to: '/orgs/$orgId/billing/$type',
						params: { orgId: createdOrg.id, type: 'plan-details' },
					})
				}
			} else if (isConsole) {
				console.warn('Organization created but missing id for navigation')
			}

			onOpenChange(false)
			onSuccess?.()
		},
		onError: (err: unknown) => {
			const errorMessage = err instanceof Error ? err.message : 'Failed to create organization'
			toast.error(errorMessage)
		},
	})

	const handleSubmit = () => {
		const trimmed = name.trim()
		const hostError = validateBrokerHost(brokerHost)
		const portError = validateBrokerPort(brokerPort)

		setValidationError(trimmed ? null : 'Organization name is required')
		setBrokerHostError(hostError)
		setBrokerPortError(portError)

		if (!trimmed || hostError || portError) {
			return
		}

		// Check organization limit before attempting creation (skip for demo deployment)
		if (!isDemoDeployment() && user) {
			const orgCount = getCreatedOrganizationsCount(user)
			if (orgCount >= ORGANIZATION_CREATION_LIMIT_PER_USER) {
				toast.error(
					`You have reached the maximum limit of ${ORGANIZATION_CREATION_LIMIT_PER_USER} organizations. Please delete an existing organization to create a new one.`,
				)
				return
			}
		}

		createMutation.mutate({
			name: trimmed,
			brokerHost: brokerHost.trim(),
			brokerPort: Number.parseInt(brokerPort.trim(), 10),
		})
	}

	const handleCancel = () => {
		onOpenChange(false)
		setName('')
		setBrokerHost('')
		setBrokerPort('')
		setValidationError(null)
		setBrokerHostError(null)
		setBrokerPortError(null)
		createMutation.reset()
	}

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			handleCancel()
		}
	}

	const displayError = validationError || (createMutation.error instanceof Error ? createMutation.error.message : null)

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent data-testid='create-organization-dialog' className={isMobile ? 'max-h-[60vh]' : 'max-h-[70vh]'}>
				<DialogHeader>
					<DialogTitle>Create Organization</DialogTitle>
				</DialogHeader>

				<div className='space-y-4 py-4'>
					<div className='flex flex-col gap-2'>
						<Label htmlFor='name' className='text-control'>
							Organization Name {RequiredIndicator}
						</Label>
						<Input
							id='name'
							data-testid='create-organization-name'
							data-kbd-field=''
							value={name}
							onChange={e => {
								setName(e.target.value)
								setValidationError(null)
							}}
							placeholder='Enter organization name'
							className='placeholder:text-control'
							disabled={createMutation.isPending}
						/>
						{displayError && (
							<p data-testid='create-organization-name-error' className='text-body text-destructive'>
								{displayError}
							</p>
						)}
					</div>
					<OrganizationBrokerFields
						idPrefix='create-organization'
						dataTestIdPrefix='create-organization'
						brokerHost={brokerHost}
						brokerPort={brokerPort}
						hostError={brokerHostError}
						portError={brokerPortError}
						disabled={createMutation.isPending}
						onBrokerHostChange={value => {
							setBrokerHost(value)
							setBrokerHostError(null)
						}}
						onBrokerPortChange={value => {
							setBrokerPort(value)
							setBrokerPortError(null)
						}}
					/>
				</div>

				<DialogFooter>
					<Button
						data-testid='create-organization-cancel'
						variant='outline'
						onClick={handleCancel}
						disabled={createMutation.isPending}>
						Cancel
					</Button>
					<Button
						data-testid='create-organization-submit'
						data-kbd-submit=''
						onClick={handleSubmit}
						disabled={createMutation.isPending || !name.trim() || !brokerHost.trim() || !brokerPort.trim()}>
						{createMutation.isPending ? 'Creating...' : 'Create'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
